import React, { useContext, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, ThemeContext } from "./context/ThemeContext";
import { LoginProvider, LoginContext } from "./context/LoginContext";
import { DestinazioneProvider, DestinazioneContext } from "./context/DestinazioneContext";
import { Button } from "react-bootstrap";
import { AlertProvider } from "./context/AlertContext";
import { useAlert } from "./context/AlertContext";
import GlobalAlert from "./components/GlobalAlert";
import InstallPrompt from "./components/InstallPrompt";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";

// Componenti
import NavigationBar from "./components/Navbar";
import SideMenu from "./components/SideMenu";
import MobileDaySelector from "./components/MobileDaySelector";

// Pagine
import Login from "./pages/Login";
import Planning from "./pages/Planning";
import Documents from "./pages/Documents";
import WeeklyPlanningTable from "./pages/WeeklyPlanningTable";
import Registrazione from "./pages/Registrazione";
import Destinazioni from "./pages/Destinazioni";
import Preordini from "./pages/Preordini";
import Smistamento from "./pages/Smistamento";
import Magazzino from "./pages/Magazzino";
import AdminSettings from "./pages/AdminSettings";
import useIsMobile from "./hooks/useIsMobile";
import { isConfiguratorOrarioValueValid } from "./utils/configuratorOrarioDisplay";

// Layout principale con menu laterale
const MainLayout = ({ children, selectedDay, setSelectedDay  }) => {
  const { darkMode } = useContext(ThemeContext);
  const isMobile = useIsMobile();
  const [sidebarVisible, setSidebarVisible] = useState(true);

  useEffect(() => {
    setSidebarVisible(!isMobile);
  }, [isMobile]);

  return (
    <>
      <div className={`main-layout ${darkMode ? 'bg-dark' : 'main-layout-light'}`} style={{ minHeight: '100vh' }}>
        <div style={{ display: 'flex', position: 'relative' }}>
        {/* Sidebar */}
        {!isMobile && sidebarVisible && (
          <div className="d-print-none">
            <SideMenu selectedDay={selectedDay} setSelectedDay={setSelectedDay} onCollapse={() => setSidebarVisible(false)} />
          </div>
        )}
        
        {/* Contenuto principale */}
        <div
          className="main-layout__main"
          style={{ 
            flex: 1, 
            marginLeft: !isMobile && sidebarVisible ? '250px' : '0',
            transition: 'margin-left 0.3s ease',
            width: '100%',
            minHeight: 'calc(100vh - var(--navbar-height))'
          }}
        >
          {/* Pulsante per mostrare il menu laterale su mobile */}
          {!isMobile && !sidebarVisible && (
            <div className="d-flex justify-content-start p-2 d-print-none">
              <Button
                variant={darkMode ? "outline-light" : "outline-dark"}
                size="sm"
                onClick={() => setSidebarVisible(true)}
                className="mb-2"
              >
                ☰ Menu
              </Button>
            </div>
          )}
          
          {/* Contenuto della pagina */}
          <div className="py-2">
            {/* Passa selectedDay, setSelectedDay e stato sidebar per ancoraggi (es. barra fissa in basso) */}
            {React.cloneElement(children, { selectedDay, setSelectedDay, sidebarVisible: !isMobile && sidebarVisible, isMobile })}
          </div>
        </div>
      </div>
      </div>
      {isMobile && (
        <MobileDaySelector
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          darkMode={darkMode}
        />
      )}
    </>
  );
};

// Componente per proteggere le rotte
const ProtectedRoute = ({ children, selectedDay, setSelectedDay }) => {
  const { isAuthenticated, loading } = useContext(LoginContext);
  const { isDestinazioneLoggedIn } = useContext(DestinazioneContext);

  // Se sta ancora caricando, mostra un messaggio o spinner
  if (loading) {
    return <div className="d-flex justify-content-center p-5">Caricamento...</div>;
  }

  // Se non è autenticato o il login destinazione non è valido, reindirizza
  if (!isAuthenticated || !isDestinazioneLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <MainLayout selectedDay={selectedDay} setSelectedDay={setSelectedDay}>{children}</MainLayout>;
};

function AppContent() {
  const { darkMode } = useContext(ThemeContext);
  const { isDestinazioneLoggedIn, isAuthenticated, companyInfo } = useContext(LoginContext);
  const { destinazione } = useContext(DestinazioneContext);
  const alert = useAlert();

  const isSuperuserLayout =
    typeof window !== 'undefined' && localStorage.getItem('isSuperuser') === 'true';
  const ddLayout = destinazione?.datiDestinazione;
  const destBarValid =
    !isSuperuserLayout &&
    String(ddLayout?.abilita_blocco ?? '').trim() === '1' &&
    isConfiguratorOrarioValueValid(ddLayout?.orarioblocco_dest);
  const hasOrarioBar =
    isAuthenticated &&
    (destBarValid || isConfiguratorOrarioValueValid(companyInfo?.orarioBlocco));
  
  // Helper per ottenere la data corrente nel fuso orario italiano
  const getItalianDate = () => {
    const date = new Date();
    const options = { timeZone: 'Europe/Rome' };
    const italianDateString = date.toLocaleString('en-US', options);
    return new Date(italianDateString);
  };

  // Funzione per ottenere il giorno corrente in italiano
  const getCurrentDay = () => {
    const today = getItalianDate();
    const dayOfWeek = today.getDay(); // 0 = domenica, 1 = lunedì, ..., 6 = sabato
    
    const dayMapping = {
      0: "domenica",
      1: "lunedi",
      2: "martedi",
      3: "mercoledi",
      4: "giovedi",
      5: "venerdi",
      6: "sabato"
    };
    
    console.log('📅 Giorno corrente:', dayOfWeek, '->', dayMapping[dayOfWeek]);

    return dayMapping[dayOfWeek];
  };
  
  const [selectedDay, setSelectedDay] = useState(getCurrentDay());

  // *** AGGIUNTO: Forza giorno corrente quando l'utente è autenticato ***
  useEffect(() => {
    if (isDestinazioneLoggedIn) {
      const currentDay = getCurrentDay();
      console.log('🔄 Utente autenticato, forzo giorno corrente:', currentDay);
      console.log('📅 Giorno precedente era:', selectedDay);
      
      if (selectedDay !== currentDay) {
        setSelectedDay(currentDay);
        console.log('✅ Giorno aggiornato da', selectedDay, 'a', currentDay);
      }
    }
  }, [isDestinazioneLoggedIn]); // Si attiva quando l'utente fa login/logout


  return (
    <Router>
      <div className="d-print-none">
        <NavigationBar />
      </div>
      <InstallPrompt />
      <GlobalAlert
        show={alert.show}
        message={alert.message}
        variant={alert.variant}
        onClose={alert.hideAlert}
      />
      <div className={`app-container ${darkMode ? "dark" : "light"} ${hasOrarioBar ? "navbar-has-orario-bar" : ""}`}>
        <Routes>
          {/* Rotta pubblica */}
          <Route path="/" element={<Login />} />
          <Route path="/registrazione" element={<Registrazione />} />

          {/* Rotte protette */}
          <Route
            path="/home"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                  <Planning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documenti/tabella-settimanale"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <WeeklyPlanningTable />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documenti"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/destinazioni"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <Destinazioni />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preordini"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <Preordini />
              </ProtectedRoute>
            }
          />
          {/* Smistamento: UI amministratore; la pagina reindirizza se !isDefaultUser */}
          <Route
            path="/smistamento"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <Smistamento />
              </ProtectedRoute>
            }
          />
          <Route
            path="/magazzino"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <Magazzino />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-settings"
            element={
              <ProtectedRoute selectedDay={selectedDay} setSelectedDay={setSelectedDay}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          {/* Reindirizzamento di default dopo login */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <LoginProvider>
      <ThemeProvider>
        <DestinazioneProvider>
          <AlertProvider>
            <AppContent />
          </AlertProvider>
        </DestinazioneProvider>
      </ThemeProvider>
    </LoginProvider>
  );
}

export default App;